import { useEffect, useRef, useState } from "react";
import { DEMO_DURATION } from "./demoScenario";

export function useDemoPlayback(duration = DEMO_DURATION) {
  const stageRef = useRef<HTMLDivElement>(null);
  const clockRef = useRef(0);
  const [time, setTime] = useState(0);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== "hidden");
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [imageReady, setImageReady] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onMotion = () => setReducedMotion(media.matches);
    const onVisibility = () => setPageVisible(document.visibilityState !== "hidden");
    media.addEventListener("change", onMotion);
    document.addEventListener("visibilitychange", onVisibility);
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting && entry.intersectionRatio >= 0.25),
      { threshold: [0, 0.25] },
    );
    if (stageRef.current) observer?.observe(stageRef.current);
    if (!observer) setInView(true);
    return () => {
      media.removeEventListener("change", onMotion);
      document.removeEventListener("visibilitychange", onVisibility);
      observer?.disconnect();
    };
  }, []);

  const running = !reducedMotion && inView && pageVisible && imageReady;

  useEffect(() => {
    if (!running) return;
    let frame: number;
    let previous: number | undefined;
    let lastPaint = 0;
    const tick = (now: number) => {
      if (previous !== undefined) clockRef.current = (clockRef.current + now - previous) % duration;
      previous = now;
      // Only the isolated player renders at 30fps; no landing-page state changes.
      if (now - lastPaint >= 1000 / 30) {
        setTime(clockRef.current);
        lastPaint = now;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [running, duration]);

  return { stageRef, time, reducedMotion, running, setImageReady };
}
